import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import {
  type InitAvo,
  initializeAnalytics as initializeAnalyticsClient,
  normalizeAnalyticsEnvironment,
  updateSystemProperties,
} from '@susu/analytics-client'

import avo, {
  ApplicationId,
  AvoEnv,
  type CustomDestination,
} from '../../../Avo/Avo'
import { version } from '../../../package.json'
import { type Environment, INSTRUMENTATION_KEY } from '../../config'
import { normalizeGbCountryCode } from '../locale'
import { type ScreenType, useBookingStore } from '../../store'

const APPLICATION_ID = ApplicationId.BOOKING_TOOL

const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const noop = () => undefined
const NOOP_DESTINATION = {
  make: noop,
  logEvent: noop,
  setUserProperties: noop,
  identify: noop,
  unidentify: noop,
  logPage: noop,
  revenue: noop,
}

const initAvo: InitAvo = (
  options,
  systemProperties,
  destinationOptions,
  destination,
) =>
  avo.initAvo(
    {
      env: options.env === 'prod' ? AvoEnv.Prod : AvoEnv.Dev,
      webDebugger: false,
      avoLogger: silentAvoLogger,
    },
    {
      ...systemProperties,
      applicationId: APPLICATION_ID,
    } as unknown as Parameters<typeof avo.initAvo>[1],
    destinationOptions,
    {
      ...destination,
      // Checked per event, not only at init: a host can switch `location` to
      // 'cn' after mount, and Google services are blocked there.
      logEvent: (eventName: string, eventProperties: unknown) => {
        if (useBookingStore.getState().location === 'cn') return
        destination.logEvent(eventName, eventProperties)
      },
    } as unknown as CustomDestination,
  )

/**
 * Wires this package's Avo client. Call it before the first `Avo.*` call —
 * `BookingToolRoot` does, synchronously on its first render, so no screen's
 * mount effect can fire into an unwired client.
 *
 * The generated client reaches its destination unguarded
 * (`Segment.logEvent(...)`), so every `Avo.*` call throws until `initAvo` has
 * run. China (Google services are blocked, so no analytics) and an init that
 * throws both end on a no-op destination instead: tracking never takes the
 * booking down. `initAvo` ignores the fallback once the real init has run.
 */
export const initializeAnalytics = (env: Environment) => {
  try {
    if (useBookingStore.getState().location === 'cn') return

    // No-ops the transport and consent script when a host already owns them
    // (the storefront initialises Segment on page load, long before the panel
    // opens; the client also skips the OneTrust script on any page that
    // already carries one) — but still registers this package's own Avo
    // client, synchronously.
    initializeAnalyticsClient({
      surface: {
        // Consumer-facing booking flow: OneTrust-gated, Website source.
        placement: 'website',
        environment: normalizeAnalyticsEnvironment(env),
      },
      applicationId: APPLICATION_ID,
      version,
      initAvo,
      // Without this the client has nowhere to push GA identity once GA4's
      // cookies settle, so the three ga* fields stay absent (D3).
      setAvoSystemProperties: avo.setSystemProperties as unknown as (
        properties: Record<string, unknown>,
      ) => void,
    })
    syncSystemProperties()
  } catch (error) {
    console.warn('Analytics initialization skipped:', error)
  } finally {
    initAvo(
      { env: 'prod' },
      { applicationId: APPLICATION_ID, version },
      {},
      NOOP_DESTINATION,
    )
  }
}

/**
 * Overrides the system properties `@susu/analytics-client` reads from the page
 * URL with the tool's own state. The standalone app has no locale path and no
 * host page, so without this `country` / `language` would be absent and
 * `page_type` would read `home`. Re-run when host props change.
 */
export const syncSystemProperties = () => {
  const { location, language, pageType, userId } = useBookingStore.getState()
  if (location === 'cn') return

  updateSystemProperties(APPLICATION_ID, {
    // RP-6083: 'uk' (CRM) -> 'gb' (Avo) at the Segment boundary.
    country: normalizeGbCountryCode(location),
    language,
    pageType: pageType ?? 'booking_tool',
    // Absent keeps the client's own value (the `uid` cookie).
    userId,
  })
}

export const loadAppInsights = () => {
  const { env } = useBookingStore.getState()
  const applicationInsights = new ApplicationInsights({
    config: {
      instrumentationKey: INSTRUMENTATION_KEY[env],
      enableCorsCorrelation: true,
      disableTelemetry: false,
      disableFetchTracking: true,
      disableAjaxTracking: true,
    },
  })

  applicationInsights.loadAppInsights()

  applicationInsights.addTelemetryInitializer((envelope) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (envelope as any).data?.baseData
    const message =
      data?.message || data?.exception?.message || envelope.name || ''
    if (typeof message === 'string' && message.includes('Script error.')) {
      return false
    }
  })

  useBookingStore.setState({ appInsights: applicationInsights })
}

/**
 * Maps each screen to its Avo step number — the same map `step_viewed` and
 * `step_completed` both read, so the two can never disagree about a step's
 * number or name. Matches the Avo schema's own step map exactly:
 * 1 store-locator, 2 employees-list, 3 timeslots, 4 user-data,
 * 5 confirmation, 6 pre-appointment-purpose.
 */
export const mapSteps: Partial<Record<ScreenType, number>> = {
  'store-locator': 1,
  'employees-list': 2,
  timeslots: 3,
  'user-data': 4,
  confirmation: 5,
  'pre-appointment-purpose': 6,
}

/**
 * The values booking events carry, read from the store when the event fires.
 * Each `Avo.*` call spreads this and adds its own event properties; the
 * generated function picks the keys its event declares and ignores the rest.
 *
 * `step` / `name` describe the current screen, so fire before a `navigate`,
 * never after.
 */
export const flowProperties = () => {
  const state = useBookingStore.getState()

  return {
    eventLabel: null,
    eventLocation: 'booking_tool',
    // UUID created once per mount (BookingToolRoot) — groups every event of
    // one booking flow.
    flowId: state.flowId ?? '',
    // The store's location code (e.g. SR-US-Atlanta-Lenox), which joins
    // dim__location.location_code. Set by the host or by StoreLocator.
    locationCode: state.locationCode ?? '',
    storeId: state.storeId ?? '',
    employeeId: state.employeeId ?? '',
    serviceId: state.serviceId ?? '',
    step: mapSteps[state.screen] ?? 0,
    name: state.screen,
  }
}
